import { useCustomersContext } from '@src/hooks/useCustomer';
import { useAppStateDispatch, useAppStateSelector } from '@src/providers/AppStateProvider';
import { getRestFailureUserMessage } from '@src/utils/rtkQuery/restFailureError';
import {
  NotificationVariants,
  useCallbackRef,
  useDynamicCallback,
  useGlobalToasts,
  type CustomerUserApiKey,
} from '@talos/kyoko';
import {
  addApiKey,
  apiKeyDeleted,
  apiKeySaved,
  fetchingKeys,
  getEffectiveObjectFilters,
  hasApiKeyDetailsChanged,
  selectDirtyApiKeyRowIds,
  setApiKeys,
} from '../state/apiKeys/apiKeysSlice';
import { diffObjectFilters } from './objectFilters';

function toApiKeyPatchBody(key: CustomerUserApiKey): Omit<CustomerUserApiKey, 'ObjectFilters'> {
  const { ObjectFilters: _objectFilters, ...rest } = key;
  return rest;
}

export function useCustomerApiKeys() {
  const {
    createCustomerUserAPIKey,
    updateCustomerUserAPIKey,
    updateCustomerUserAPIKeyPermissions,
    deleteCustomerUserAPIKey,
    getCustomerUserAPIKeys,
  } = useCustomersContext();

  const { add: addToast } = useGlobalToasts();
  const state = useAppStateSelector(state => state.customerUserDrawer);
  const dirtyApiKeyRowIds = useAppStateSelector(selectDirtyApiKeyRowIds);
  const dispatch = useAppStateDispatch();

  const saveApiKeys = useDynamicCallback(async () => {
    const requests: Promise<unknown>[] = [];
    const apiKeysState = state.apiKeys;
    const customerUserId = state.userDetails.customerUserId;
    const keys = apiKeysState.keys;
    if (keys) {
      keys.forEach(key => {
        const apiKeyId = key.CustomerUserApiKeyID;
        if (apiKeysState.keysToDelete.includes(apiKeyId)) {
          customerUserId &&
            requests.push(
              deleteCustomerUserAPIKey(customerUserId, apiKeyId).then(() => {
                dispatch(apiKeyDeleted(apiKeyId));
              })
            );
        } else if (apiKeysState.dirtyKeys.includes(apiKeyId)) {
          const { InsertObjectFilters, RemoveObjectFilters } = diffObjectFilters(
            apiKeysState.savedObjectFiltersByKeyId[apiKeyId],
            getEffectiveObjectFilters(apiKeysState, apiKeyId)
          );

          const savePromises: Promise<unknown>[] = [];
          if (hasApiKeyDetailsChanged(apiKeysState, apiKeyId)) {
            savePromises.push(updateCustomerUserAPIKey(toApiKeyPatchBody(key)));
          }
          if ((InsertObjectFilters.length > 0 || RemoveObjectFilters.length > 0) && customerUserId) {
            savePromises.push(
              updateCustomerUserAPIKeyPermissions(customerUserId, apiKeyId, {
                InsertObjectFilters,
                RemoveObjectFilters,
              })
            );
          }

          if (savePromises.length > 0) {
            requests.push(
              Promise.all(savePromises).then(() => {
                dispatch(apiKeySaved(apiKeyId));
              })
            );
          }
        }
      });

      try {
        await Promise.all(requests);
      } catch (e) {
        throw new Error(getRestFailureUserMessage(e) ?? 'Customer User API Key could not be saved.');
      }
    }
  });

  const createAPIKey = useCallbackRef(async (label?: string) => {
    if (!state.userDetails.customerUserId) {
      return;
    }
    const { data } = await createCustomerUserAPIKey(state.userDetails.customerUserId, label);
    dispatch(addApiKey(data[0]));
  });

  const fetchApiKeys = useDynamicCallback((customerUserID: string) => {
    if (!state.apiKeys.fetching && !state.apiKeys.keys) {
      dispatch(fetchingKeys());
      getCustomerUserAPIKeys(customerUserID)
        .then(({ data }: { data: CustomerUserApiKey[] }) => {
          dispatch(setApiKeys(data));
        })
        .catch(({ message }) => {
          addToast({
            text: message ?? 'Could not fetch API keys',
            variant: NotificationVariants.Negative,
          });
        });
    }
  });

  return {
    apiKeys: state.apiKeys.keys,
    deletedApiKeys: state.apiKeys.keysToDelete,
    selectedApiKeyId: state.apiKeys.selectedApiKeyId,
    dirtyApiKeyRowIds,
    saveApiKeys,
    fetchApiKeys,
    createAPIKey,
    fetching: state.apiKeys.fetching,
  };
}
